import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useCurrentSpaceId } from '@/entities/teamspace';

import { API } from '@/shared/api';
import { Settings } from '@/shared/types/Settings';

import { defaultSettings } from '../lib/defaultSettings';
import { settingsAdapter } from '../lib/settingsAdapter';
import { USER_QUERY_KEYS } from '../lib/userQueryKeys';
import { useIsAuthenticated } from './useCurrentUser';

const fetchSettings = async (isAuthenticated: boolean, spaceId: string): Promise<Settings> => {
  if (spaceId) {
    const response = await API.getSpaceSettings(spaceId);
    return settingsAdapter(response);
  }

  const settingsApi = isAuthenticated ? API.getUserSettings : API.getAppSettings;
  const response = await settingsApi();

  return settingsAdapter(response);
};

export const useSettingsQuery = () => {
  const isAuthenticated = useIsAuthenticated();
  const spaceId = useCurrentSpaceId();

  // A superuser in a customer's space gets the owner's plan too: localization_app checks that plan before it runs
  // anything there, and the admin Customers tab links to the customer's space.
  return useQuery<Settings>({
    queryFn: () => fetchSettings(isAuthenticated, spaceId),
    queryKey: USER_QUERY_KEYS.userSettings(isAuthenticated, spaceId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useSettings = (): Settings => {
  const query = useSettingsQuery();
  return useMemo(() => query.data ?? defaultSettings, [query.data]);
};

export const useInitSettings = () =>
  useQuery({
    queryFn: () => fetchSettings(false, ''),
    queryKey: USER_QUERY_KEYS.userSettings(),
    refetchOnMount: false,
    retry: 3,
    retryDelay: (attempt) => attempt * 1000,
  });

export const useUserSettings = () => {
  const query = useQuery({
    queryFn: () => fetchSettings(true, ''),
    queryKey: USER_QUERY_KEYS.userSettings(true, ''),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => query.data ?? defaultSettings, [query.data]);
};
